import { useStore } from '../../store'
import { Toggle } from '../ui'
import { ops } from '../../lib/ops'

/**
 * Coverage toggle: dot + "Covering"/"Not covering" + a 26x14 switch on a
 * green-subtle pill (amber when off).
 *
 * MOVED (task-2 brief, docs/design-handoff/tossker-redesign/README.md §1/§6,
 * binding decision 6): this used to render in the header, next to the
 * search field. It now renders inside `InitialsMenu`'s state block, as the
 * "Covering the queue" row — the header keeps only the aggregate signal, as
 * a green dot on the account avatar. The operation this control calls is
 * completely unchanged; only where it's mounted moved.
 *
 * ALSO DROPPED as part of that move: the overlapping 22px avatars for other
 * staff currently covering that used to render immediately to this pill's
 * right. That was genuinely redundant real estate inside a 320px menu —
 * `SignedInPeers` (in the header) already draws a green covering dot on
 * every online peer's own avatar, which is the same fact. Dropping it here
 * is a display simplification, not a behaviour change: nothing this
 * component calls or writes was touched.
 *
 * Per docs/design-handoff/reference/communicatorv2.3.md §9 "Coverage is the
 * master switch": coverage reads as off only when nobody at all is covering.
 * My own toggle controls only my own `covering` flag; the dot/label reflect
 * the aggregate (mine OR any peer's — see the task-38 follow-up note below
 * for why this is any covering peer, not dispatch-role peers only), so a
 * colleague covering while I step away never reads as "coverage off".
 *
 * Task 38: the switch used to write `covering` straight into the Zustand
 * store and nowhere else — a dispatcher's browser said "Covering" while the
 * `staff` table (and therefore every client's "someone's here" signal, v2.3
 * §9) never heard about it. `onChange` now calls `ops.setCoverage` (POST
 * /ops/coverage) and waits for the server's own answer before touching the
 * store at all: on success the store is set to exactly what the server
 * persisted (never to the locally-requested value, in case the two ever
 * differ), and on a refusal or network failure the store is left completely
 * untouched — the switch snaps back to whatever it actually was, rather than
 * sitting on a flipped position the database never agreed to. That refusal
 * still surfaces to the dispatcher as a toast, so a failed flip is never
 * silent.
 *
 * Task-38 follow-up — OWNER RULING, role and covering are orthogonal axes:
 * **role** (`dispatch`/`tech`) is a person's primary job — it drives badge
 * colour (dispatch/tech, `--role-dispatch`/`--role-tech` in tokens.css — gold
 * and teal in the Tossker themes as of task 2, the original pink/blue in
 * Legacy) and notification scoping, never availability. **Covering** is "am
 * I at the desk right now" — any covering staff member, tech or dispatcher,
 * makes clients see someone available; a tech turning on covering acts as a
 * temporary dispatcher for availability purposes only, without
 * reclassifying. So the pill's aggregate counts ANY covering peer regardless
 * of role, matching `routes/client-state.ts`'s `readCoverage` (`where
 * covering = true`) exactly — and everywhere an avatar for a covering peer
 * renders (`SignedInPeers`, in the header) it still carries that peer's own
 * real role colour: covering never changes badge colour, only the fill
 * (typing/reading) does.
 *
 * This is a deliberate, owner-approved divergence from design doc §9's
 * current text ("Availability is a property of the dispatch role. Flip into
 * dispatch, you are available to the queue") — decoupled because a tech
 * covering a lunch break should not have to reclassify as a dispatcher, and
 * a dispatcher who has stepped away should not stop being a dispatcher.
 * **The design doc itself has not been updated to reflect this** — flagged
 * here and in the task-38 follow-up report rather than silently
 * contradicted.
 */
export function CoverageToggle() {
  const covering = useStore((s) => s.covering)
  const peers = useStore((s) => s.peers)
  const set = useStore((s) => s.set)
  const pushToast = useStore((s) => s.pushToast)

  const anyoneCovering = covering || peers.some((p) => p.covering)

  const handleChange = (checked: boolean) => {
    void ops
      .setCoverage(checked)
      .then((result) => {
        set({ covering: result.covering })
        // SAID ONCE, WHEN IT CHANGES — not held on screen forever. Not
        // covering is the normal state for a tech, and a permanent orange
        // banner about the normal state is just something to learn to ignore.
        // The header chip still carries the standing signal (it turns warn
        // when nobody at all is covering), which is the part worth keeping.
        pushToast({
          text: result.covering
            ? 'Coverage on — new conversations can come to you.'
            : 'Coverage off — new conversations will queue for whoever is covering.',
          tone: result.covering ? 'ok' : 'info',
        })
      })
      .catch(() => {
        // Refused or unreachable: `covering` is left exactly as it was — a
        // toggle that appears to have worked without the server's agreement
        // is the precise bug this task exists to close.
        pushToast({ text: 'Could not update your coverage. Try again.', tone: 'bad' })
      })
  }

  return (
    <div
      className={[
        'flex items-center gap-2 rounded-[20px] px-2.5 py-1',
        anyoneCovering ? 'bg-ok-subtle' : 'bg-warn-subtle',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={['h-[6px] w-[6px] rounded-full', anyoneCovering ? 'bg-ok' : 'bg-warn'].join(' ')}
      />
      <span className={['text-[13px] font-medium', anyoneCovering ? 'text-ok' : 'text-warn'].join(' ')}>
        {anyoneCovering ? 'Covering' : 'Not covering'}
      </span>
      <Toggle checked={covering} onChange={handleChange} aria-label="Toggle my coverage" />
    </div>
  )
}
